import { Alert, AlertTitle, Typography } from '@mui/material';
import React, { forwardRef } from 'react';

import CheckIcon from '../../assets/icons/Check';
import InfoFillIcon from '../../assets/icons/InfoIconBackgroundFill';
import Warning from '../../assets/icons/Warning';
import WarningRound from '../../assets/icons/WarningRound';
import colors from '../../constants/colors';
import { useSnackbarsContext } from '../../store/SnackbarProvider';
import { Snackbar as SnackbarType } from '../../store/snackbarReducer';
import { AlertCloseButton } from '../styledComponents/IconButtons';

export const snackbarMessageTextStyles = {
  fontFamily: 'Cabin',
  fontKerning: 'none',
  fontSize: { mobile: 16, tablet: 18 },
  fontWeight: 400,
  lineHeight: '25.2px',
  letterSpacing: 0.4,
};

const MessageText = ({
  content,
  className,
}: {
  content: string | React.ReactNode;
  className: string | undefined;
}) => (
  <Typography
    className={className}
    sx={{
      ...snackbarMessageTextStyles,
    }}
  >
    {content}
  </Typography>
);

interface SnackbarProps extends Omit<SnackbarType, 'key'> {
  id: string;
}

const Snackbar = forwardRef<HTMLDivElement, SnackbarProps>(
  (
    {
      message,
      title,
      content,
      dataTestId,
      className,
      dismissible = true,
      variant = 'success',
      id,
    }: SnackbarProps,
    ref
  ) => {
    const { closeSnackbar } = useSnackbarsContext();

    const snackbarColors = {
      success: {
        backgroundColor: colors.csaaLightGreen,
        borderColor: colors.csaaSpectrumGreen50,
      },
      error: {
        backgroundColor: colors.csaaLightRed,
        borderColor: colors.csaaSpectrumRed50,
      },
      warning: {
        // not sure why we don't have colors for these yet... couldn't find their names in Figma.
        backgroundColor: '#FFFAE0',
        borderColor: '#EBA62F',
      },
      info: {
        backgroundColor: colors.csaaBlueInfo,
        borderColor: colors.csaaSpectrumBlue50,
      },
    };

    const snackBarIcons = {
      success: <CheckIcon sx={{ alignSelf: 'center', mr: 0.5 }} />,
      error: <Warning sx={{ alignSelf: 'center', mr: 0.5 }} />,
      warning: (
        <WarningRound sx={{ alignSelf: 'center', mr: 0.5 }} fill="#EBA62F" />
      ),
      info: (
        <InfoFillIcon
          sx={{ alignSelf: 'center', mr: 0.5 }}
          fill={colors.csaaSpectrumBlue50}
        />
      ),
    };

    return (
      <Alert
        data-testid={dataTestId}
        onClose={dismissible ? () => closeSnackbar(id) : undefined}
        severity={variant}
        iconMapping={snackBarIcons}
        sx={{
          width: 1,
          border: 2,
          borderColor: snackbarColors[variant].borderColor,
          backgroundColor: snackbarColors[variant].backgroundColor,
          borderRadius: 2,
          color: 'text.primary',
          py: 2,
          px: { mobile: 2, tablet: 4 },
          '& .MuiAlert-message': {
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          },
        }}
        action={
          dismissible ? (
            <AlertCloseButton
              aria-label="close"
              baseColor={snackbarColors[variant].borderColor}
              color="inherit"
              sx={{ alignSelf: 'center' }}
              data-testid="errorSnackbarIconButton"
              onClick={() => closeSnackbar(id)}
            />
          ) : undefined
        }
      >
        {title && (
          <AlertTitle
            sx={{
              fontWeight: 700,
              fontSize: { mobile: 16, tablet: 18 },
              color: 'text.primary',
              lineHeight: '25.2px',
              letterSpacing: 0.4,
              mb: 0.5,
            }}
          >
            {title}
          </AlertTitle>
        )}
        {(content || message) && (
          <MessageText className={className} content={content ?? message} />
        )}
      </Alert>
    );
  }
);

export default Snackbar;
